import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Text,
  SimpleGrid,
  Button,
  VStack,
  HStack,
  useColorModeValue,
  Icon,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Spinner,
  Alert,
  AlertIcon,
  Image,
} from '@chakra-ui/react';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CalendarIcon,
} from '@chakra-ui/icons';
import { FaMagic, FaExternalLinkAlt } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import axios from 'axios';

const MagicCalendar = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const bgColor = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.800', 'white');
  const accentColor = useColorModeValue('#ffcc00', '#ffcc00');
  const secondaryColor = useColorModeValue('#3eb297', '#3eb297');

  // Generate consistent colors for subcategories
  const getSubcategoryColor = (subcategory) => {
    if (!subcategory) return accentColor;

    // Predefined colors for common Magic subcategories
    const colorMap = {
      Commander: '#FF6B6B', // Red
      Draft: '#4ECDC4', // Teal
      Pauper: '#45B7D1', // Blue
      Standard: '#96CEB4', // Green
      Modern: '#FFEAA7', // Yellow
      Legacy: '#DDA0DD', // Plum
      Vintage: '#98D8C8', // Mint
      Limited: '#F7DC6F', // Light Yellow
      Constructed: '#BB8FCE', // Light Purple
      Sealed: '#85C1E9', // Light Blue
    };

    // If we have a predefined color, use it
    if (colorMap[subcategory]) {
      return colorMap[subcategory];
    }

    // Generate a consistent color based on subcategory name
    let hash = 0;
    for (let i = 0; i < subcategory.length; i += 1) {
      hash = subcategory.charCodeAt(i) + (hash * 32 - hash);
    }

    // Convert hash to HSL and generate a vibrant color
    const hue = Math.abs(hash) % 360;
    return `hsl(${hue}, 70%, 60%)`;
  };

  // Fetch Magic events from CMS
  const fetchMagicEvents = async () => {
    try {
      setLoading(true);

      // Fetch all pages of events
      let allEvents = [];
      let page = 1;
      let hasMorePages = true;
      const pageSize = 100; // Increased page size to reduce number of requests

      // eslint-disable-next-line no-await-in-loop
      while (hasMorePages) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const response = await axios.get(
            `/api/eventi?page=${page}&pageSize=${pageSize}`
          );

          const currentPageEvents = response.data.events || [];

          if (currentPageEvents.length > 0) {
            allEvents = allEvents.concat(currentPageEvents);
          }

          // Check if there are more pages
          const pagination = response.data.meta?.pagination;
          if (pagination && pagination.page < pagination.pageCount) {
            page += 1;
          } else {
            hasMorePages = false;
          }
        } catch (pageError) {
          console.error(`Error fetching page ${page}:`, pageError);
          hasMorePages = false;
          // Continue with events we've fetched so far
        }
      }

      // Filter events that have macroCategory === 'MAGIC'
      const magicEvents = allEvents.filter((event) => {
        return event.macroCategory === 'MAGIC';
      });

      setEvents(magicEvents);
    } catch (err) {
      setError('Errore nel caricamento degli eventi Magic');
      console.error('Error fetching Magic events:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMagicEvents();
  }, []);

  // Calendar navigation
  const goToPreviousMonth = () => {
    setCurrentDate(
      (prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1)
    );
  };

  const goToNextMonth = () => {
    setCurrentDate(
      (prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1)
    );
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Get calendar data
  const getCalendarData = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const calendar = [];
    const current = new Date(startDate);

    while (current <= lastDay || current.getDay() !== 0) {
      calendar.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }

    return calendar;
  };

  // Get events for a specific date
  const getEventsForDate = (date) => {
    return events.filter((event) => {
      const eventDate = new Date(event.Data);
      return eventDate.toDateString() === date.toDateString();
    });
  };

  // Check if date is today
  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  // Check if date is current month
  const isCurrentMonth = (date) => {
    return date.getMonth() === currentDate.getMonth();
  };

  // Format date and time for event display
  const formatEventDateTime = (event) => {
    const eventDate = new Date(event.Data);
    const dateStr = eventDate.toLocaleDateString('it-IT', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

    if (event.Ora_inizio) {
      // Extract only the hour from "15:00:00.000" -> "15"
      const hour = event.Ora_inizio.split(':')[0];
      return `${dateStr} alle ${hour}`;
    }

    return dateStr;
  };

  // Open event details modal
  const openEventModal = (event) => {
    setSelectedEvent(event);
    onOpen();
  };

  if (loading) {
    return (
      <Box textAlign="center" py={8}>
        <Spinner size="xl" color={accentColor} />
        <Text mt={4} color={textColor}>
          Caricamento eventi Magic...
        </Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        {error}
      </Alert>
    );
  }

  const calendarData = getCalendarData();
  const monthNames = [
    'Gennaio',
    'Febbraio',
    'Marzo',
    'Aprile',
    'Maggio',
    'Giugno',
    'Luglio',
    'Agosto',
    'Settembre',
    'Ottobre',
    'Novembre',
    'Dicembre',
  ];

  return (
    <Box>
      {/* Calendar Header */}
      <Box mb={6} textAlign="center">
        <Heading
          as="h2"
          size={{ base: 'md', md: 'lg' }}
          color="white"
          mb={4}
          fontWeight="800"
        >
          <Icon as={FaMagic} mr={{ base: 2, md: 3 }} color={accentColor} />
          <Text display={{ base: 'block', sm: 'inline' }}>
            Calendario Eventi Magic
          </Text>
        </Heading>

        {/* Upcoming Events List - Moved here */}
        {events.length > 0 && (
          <Box
            bg={bgColor}
            borderRadius="lg"
            boxShadow="md"
            p={{ base: 4, md: 6 }}
            mb={4}
          >
            <Heading as="h3" size="md" color={textColor} mb={4}>
              Prossimi Eventi Magic
            </Heading>

            <VStack spacing={{ base: 2, md: 3 }} align="stretch">
              {events
                .filter((event) => new Date(event.Data) >= new Date())
                .sort((a, b) => new Date(a.Data) - new Date(b.Data))
                .slice(0, 5)
                .map((event) => (
                  <Box
                    key={`upcoming-event-${event.name}-${event.id || Math.random()}`}
                    p={{ base: 3, md: 4 }}
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="md"
                    _hover={{ borderColor: accentColor }}
                    transition="all 0.2s"
                  >
                    <VStack
                      align="stretch"
                      spacing={{ base: 2, md: 3 }}
                      display={{ base: 'block', md: 'none' }}
                    >
                      {/* Mobile Layout */}
                      <HStack align="start" spacing={3}>
                        {event.thumbnail && (
                          <Image
                            src={event.thumbnail.url}
                            alt={event.name}
                            boxSize={{ base: '50px', sm: '60px' }}
                            objectFit="cover"
                            borderRadius="md"
                            fallbackSrc="/placeholder-event.jpg"
                          />
                        )}
                        <VStack align="start" spacing={1} flex={1}>
                          <Heading as="h4" size="sm" color={textColor}>
                            🎯 {event.subCategory || event.Nome}
                          </Heading>
                          <Text fontSize="sm" color="gray.600">
                            📅 {formatEventDateTime(event)}
                          </Text>
                          {event.location && (
                            <Text fontSize="sm" color="gray.500">
                              📍 {event.location}
                            </Text>
                          )}
                          <Text
                            fontSize="sm"
                            color="gray.500"
                            fontWeight="bold"
                          >
                            {event.prezzo && event.prezzo > 0
                              ? `💰 €${event.prezzo.toFixed(2)}`
                              : '🆓 Gratis'}
                          </Text>
                        </VStack>
                      </HStack>

                      <HStack spacing={2} justify="center">
                        <Button
                          size="sm"
                          colorScheme="yellow"
                          variant="outline"
                          onClick={() => openEventModal(event)}
                          flex={1}
                        >
                          Dettagli
                        </Button>
                        <Button
                          as={Link}
                          to={`/event/${event.urlTitle}`}
                          size="sm"
                          colorScheme="yellow"
                          rightIcon={<FaExternalLinkAlt />}
                          flex={1}
                        >
                          Vai all&apos;Evento
                        </Button>
                      </HStack>
                    </VStack>

                    {/* Desktop Layout */}
                    <HStack
                      justify="space-between"
                      align="start"
                      display={{ base: 'none', md: 'flex' }}
                    >
                      <VStack align="start" spacing={1} flex={1}>
                        <HStack align="start" spacing={3}>
                          {event.thumbnail && (
                            <Image
                              src={event.thumbnail.url}
                              alt={event.name}
                              boxSize="60px"
                              objectFit="cover"
                              borderRadius="md"
                              fallbackSrc="/placeholder-event.jpg"
                            />
                          )}
                          <VStack align="start" spacing={1}>
                            <Heading as="h4" size="sm" color={textColor}>
                              🎯 {event.subCategory || event.Nome}
                            </Heading>
                            <Text fontSize="sm" color="gray.600">
                              📅 {formatEventDateTime(event)}
                            </Text>
                            {event.location && (
                              <Text fontSize="sm" color="gray.500">
                                📍 {event.location}
                              </Text>
                            )}
                            <Text
                              fontSize="sm"
                              color="gray.500"
                              fontWeight="bold"
                            >
                              {event.prezzo && event.prezzo > 0
                                ? `💰 €${event.prezzo.toFixed(2)}`
                                : '🆓 Gratis'}
                            </Text>
                          </VStack>
                        </HStack>
                      </VStack>

                      <HStack spacing={2}>
                        <Button
                          size="sm"
                          colorScheme="yellow"
                          variant="outline"
                          onClick={() => openEventModal(event)}
                        >
                          Dettagli
                        </Button>
                        <Button
                          as={Link}
                          to={`/event/${event.urlTitle}`}
                          size="sm"
                          colorScheme="yellow"
                          rightIcon={<FaExternalLinkAlt />}
                        >
                          Vai all&apos;Evento
                        </Button>
                      </HStack>
                    </HStack>
                  </Box>
                ))}
            </VStack>
          </Box>
        )}

        {/* Month Navigation */}
        <VStack spacing={{ base: 2, md: 4 }} mb={4} marginTop={10}>
          <HStack
            justify="center"
            spacing={{ base: 2, md: 4 }}
            flexWrap="wrap"
            w="full"
          >
            <Button
              onClick={goToPreviousMonth}
              leftIcon={<ChevronLeftIcon />}
              colorScheme="yellow"
              variant="outline"
              size={{ base: 'xs', md: 'sm' }}
            >
              <Text display={{ base: 'none', sm: 'block' }}>
                Mese Precedente
              </Text>
              <Text display={{ base: 'block', sm: 'none' }}>←</Text>
            </Button>

            <Heading
              as="h3"
              size={{ base: 'sm', md: 'md' }}
              color="white"
              textAlign="center"
              minW={{ base: '120px', sm: 'auto' }}
              fontWeight="700"
            >
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </Heading>

            <Button
              onClick={goToNextMonth}
              rightIcon={<ChevronRightIcon />}
              colorScheme="yellow"
              variant="outline"
              size={{ base: 'xs', md: 'sm' }}
            >
              <Text display={{ base: 'none', sm: 'block' }}>
                Mese Successivo
              </Text>
              <Text display={{ base: 'block', sm: 'none' }}>→</Text>
            </Button>
          </HStack>

          <Button
            onClick={goToToday}
            leftIcon={<CalendarIcon />}
            colorScheme="yellow"
            size={{ base: 'xs', md: 'sm' }}
          >
            Oggi
          </Button>
        </VStack>
      </Box>

      {/* Calendar Grid */}
      <Box
        bg={bgColor}
        borderRadius="lg"
        boxShadow="md"
        p={{ base: 3, md: 6 }}
        mb={6}
        overflowX="auto"
      >
        {/* Weekday Headers */}
        <SimpleGrid
          columns={{ base: 7, sm: 7 }}
          spacing={{ base: 0.5, md: 1 }}
          mb={2}
          minW={{ base: '350px', sm: 'auto' }}
        >
          {['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'].map((day) => (
            <Box
              key={day}
              textAlign="center"
              fontWeight="bold"
              color={secondaryColor}
              py={{ base: 1, md: 2 }}
              fontSize={{ base: 'xs', md: 'sm' }}
            >
              {day}
            </Box>
          ))}
        </SimpleGrid>

        {/* Calendar Days */}
        <SimpleGrid
          columns={{ base: 7, sm: 7 }}
          spacing={{ base: 0.5, md: 1 }}
          minW={{ base: '350px', sm: 'auto' }}
        >
          {calendarData.map((date) => {
            const dayEvents = getEventsForDate(date);
            const isCurrentMonthDay = isCurrentMonth(date);
            const isTodayDate = isToday(date);

            return (
              <Box
                key={`calendar-day-${date.getTime()}`}
                minH={{ base: '80px', sm: '100px' }}
                p={{ base: 1, md: 2 }}
                border="1px solid"
                borderColor={isTodayDate ? accentColor : 'gray.200'}
                bg={isTodayDate ? `${accentColor}20` : 'transparent'}
                borderRadius="md"
                position="relative"
                cursor={dayEvents.length > 0 ? 'pointer' : 'default'}
                onClick={() =>
                  dayEvents.length > 0 && dayEvents.length === 1
                    ? openEventModal(dayEvents[0])
                    : null
                }
                _hover={dayEvents.length > 0 ? { bg: 'gray.50' } : {}}
              >
                {/* Date Number */}
                <Text
                  fontSize={{ base: 'xs', md: 'sm' }}
                  fontWeight="bold"
                  color={isCurrentMonthDay ? textColor : 'gray.400'}
                  textAlign="center"
                  mb={1}
                >
                  {date.getDate()}
                </Text>

                {/* Events */}
                {dayEvents.map((event) => {
                  const eventColor = getSubcategoryColor(event.subCategory);
                  return (
                    <Box
                      key={`event-${event.Nome}-${event.id || Math.random()}`}
                      bg={eventColor}
                      color="white"
                      p={{ base: 0.5, md: 1 }}
                      borderRadius="sm"
                      fontSize={{ base: '2xs', md: 'xs' }}
                      mb={1}
                      cursor="pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        openEventModal(event);
                      }}
                      _hover={{ bg: `${eventColor}dd` }}
                    >
                      <Text
                        noOfLines={{ base: 1, md: 2 }}
                        fontWeight="bold"
                        fontSize={{ base: '2xs', md: 'xs' }}
                      >
                        {event.subCategory || event.Nome}
                      </Text>
                      {dayEvents.length > 1 && (
                        <Text
                          fontSize={{ base: '2xs', md: 'xs' }}
                          opacity={0.8}
                        >
                          +{dayEvents.length - 1} altri
                        </Text>
                      )}
                    </Box>
                  );
                })}
              </Box>
            );
          })}
        </SimpleGrid>
      </Box>

      {/* Event Details Modal */}
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size={{ base: 'sm', md: 'lg' }}
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent mx={{ base: 2, md: 0 }}>
          <ModalHeader>
            <HStack>
              <Icon as={FaMagic} color={accentColor} />
              <Text fontSize={{ base: 'sm', md: 'md' }}>Dettagli Evento</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />

          {selectedEvent && (
            <ModalBody>
              <VStack spacing={{ base: 3, md: 4 }} align="stretch">
                <Box>
                  <VStack
                    align="start"
                    spacing={{ base: 2, md: 4 }}
                    mb={3}
                    display={{ base: 'block', md: 'none' }}
                  >
                    {/* Mobile Layout */}
                    {selectedEvent.thumbnail && (
                      <Image
                        src={selectedEvent.thumbnail.url}
                        alt={selectedEvent.Nome}
                        boxSize="60px"
                        objectFit="cover"
                        borderRadius="md"
                        fallbackSrc="/placeholder-event.jpg"
                      />
                    )}
                    <VStack align="start" spacing={1}>
                      <Heading as="h3" size="sm" color={textColor}>
                        {selectedEvent.Nome}
                      </Heading>
                      <Text color="gray.600" fontSize="sm">
                        📅 {formatEventDateTime(selectedEvent)}
                      </Text>
                      {selectedEvent.location && (
                        <Text color="gray.600" fontSize="sm">
                          📍 {selectedEvent.location}
                        </Text>
                      )}
                      <Text color="gray.600" fontSize="sm" fontWeight="bold">
                        {selectedEvent.prezzo && selectedEvent.prezzo > 0
                          ? `💰 €${selectedEvent.prezzo.toFixed(2)}`
                          : '🆓 Gratis'}
                      </Text>
                    </VStack>
                  </VStack>

                  <HStack
                    align="start"
                    spacing={4}
                    mb={3}
                    display={{ base: 'none', md: 'flex' }}
                  >
                    {/* Desktop Layout */}
                    {selectedEvent.thumbnail && (
                      <Image
                        src={selectedEvent.thumbnail.url}
                        alt={selectedEvent.Nome}
                        boxSize="80px"
                        objectFit="cover"
                        borderRadius="md"
                        fallbackSrc="/placeholder-event.jpg"
                      />
                    )}
                    <VStack align="start" spacing={2}>
                      <Heading as="h3" size="md" color={textColor}>
                        {selectedEvent.Nome}
                      </Heading>
                      <Text color="gray.600">
                        📅 {formatEventDateTime(selectedEvent)}
                      </Text>
                      {selectedEvent.location && (
                        <Text color="gray.600">
                          📍 {selectedEvent.location}
                        </Text>
                      )}
                      <Text color="gray.600" fontWeight="bold">
                        {selectedEvent.prezzo && selectedEvent.prezzo > 0
                          ? `💰 €${selectedEvent.prezzo.toFixed(2)}`
                          : '🆓 Gratis'}
                      </Text>
                    </VStack>
                  </HStack>

                  {selectedEvent.description && (
                    <Text
                      color="gray.700"
                      mt={3}
                      fontSize={{ base: 'sm', md: 'md' }}
                    >
                      {selectedEvent.description}
                    </Text>
                  )}
                </Box>

                {selectedEvent.urlTitle && (
                  <Box>
                    <Text
                      fontWeight="bold"
                      mb={2}
                      fontSize={{ base: 'sm', md: 'md' }}
                    >
                      Per maggiori informazioni:
                    </Text>
                    <Button
                      as={Link}
                      to={`/event/${selectedEvent.urlTitle}`}
                      colorScheme="yellow"
                      rightIcon={<FaExternalLinkAlt />}
                      w="full"
                      size={{ base: 'sm', md: 'md' }}
                    >
                      Vai alla Pagina Evento
                    </Button>
                  </Box>
                )}
              </VStack>
            </ModalBody>
          )}

          <ModalFooter>
            <Button
              onClick={onClose}
              colorScheme="gray"
              size={{ base: 'sm', md: 'md' }}
            >
              Chiudi
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default MagicCalendar;
